import type { Job, JobItemKind, JobKind, JobReason, JobStatus } from '../api/types';
import { dt } from './format';

/** Stavy zakázky v pořadí, jak jde práce; „předáno" a „zrušeno" uzavírají. */
export const JOB_STATUSES: { key: JobStatus; label: string; short: string; pill: string }[] = [
  { key: 'received', label: 'Přijato', short: 'Přijato', pill: 'unpaid' },
  { key: 'diagnosis', label: 'Diagnostika', short: 'Diagnostika', pill: 'unpaid' },
  {
    key: 'waiting_approval',
    label: 'Čeká na schválení zákazníkem',
    short: 'Čeká na zákazníka',
    pill: 'partial',
  },
  { key: 'waiting_parts', label: 'Čeká na díl', short: 'Čeká na díl', pill: 'partial' },
  { key: 'in_progress', label: 'V opravě', short: 'V opravě', pill: 'sent' },
  { key: 'ready', label: 'Připraveno k vyzvednutí', short: 'K vyzvednutí', pill: 'paid' },
  { key: 'done', label: 'Předáno', short: 'Předáno', pill: 'settled' },
  { key: 'cancelled', label: 'Zrušeno', short: 'Zrušeno', pill: 'cancelled' },
];

export const ACTIVE_JOB_STATUSES: JobStatus[] = [
  'received',
  'diagnosis',
  'waiting_approval',
  'waiting_parts',
  'in_progress',
];

export const jobStatus = (s: JobStatus) =>
  JOB_STATUSES.find((x) => x.key === s) ?? { key: s, label: s, short: s, pill: 'draft' };

export const isActiveJob = (s: JobStatus) => ACTIVE_JOB_STATUSES.includes(s);
export const isClosedJob = (s: JobStatus) => s === 'done' || s === 'cancelled';

export const JOB_KINDS: { key: JobKind; label: string }[] = [
  { key: 'repair', label: 'Oprava' },
  { key: 'order', label: 'Zakázka' },
  { key: 'other', label: 'Jiné' },
];

export const JOB_ITEM_KINDS: { key: JobItemKind; label: string }[] = [
  { key: 'part', label: 'Díl' },
  { key: 'labor', label: 'Práce' },
  { key: 'other', label: 'Jiné' },
];

export const JOB_REASONS: Record<JobReason, string> = {
  overdue: 'po termínu',
  due_today: 'termín dnes',
  waiting_approval: 'čeká na zákazníka',
  uninvoiced: 'předáno bez faktury',
  pickup: 'nevyzvednuto',
  unassigned: 'bez technika',
};

const dayWord = (n: number) => (n === 1 ? 'den' : n >= 2 && n <= 4 ? 'dny' : 'dní');

/** Termín jednou větou a tón: „za 2 dny", „dnes", „3 dny po termínu". */
export function jobDue(job: Pick<Job, 'dueAt' | 'daysLeft' | 'status' | 'readyAt'>): {
  text: string;
  tone: 'late' | 'soon' | 'calm' | 'none';
} {
  if (!isActiveJob(job.status)) {
    if (job.status === 'ready' && job.readyAt) {
      return { text: `hotovo ${dt(job.readyAt)}`, tone: 'calm' };
    }
    return { text: '', tone: 'none' };
  }
  if (!job.dueAt || job.daysLeft === null) return { text: 'bez termínu', tone: 'none' };
  const d = job.daysLeft;
  if (d < 0) return { text: `${-d} ${dayWord(-d)} po termínu`, tone: 'late' };
  if (d === 0) return { text: 'termín dnes', tone: 'soon' };
  if (d === 1) return { text: 'termín zítra', tone: 'soon' };
  return { text: `termín ${dt(job.dueAt)}`, tone: 'calm' };
}

/** „iPhone 13 – nejde displej" do seznamů. */
export function jobSubject(job: Pick<Job, 'title' | 'deviceName' | 'problem'>): string {
  const head = job.title || job.deviceName;
  const tail = job.problem.split('\n')[0] ?? '';
  return [head, tail && tail !== head ? tail : ''].filter(Boolean).join(' – ') || 'Zakázka';
}

/** Kdo dělá: část e-mailu před zavináčem. */
export const who = (email: string | null | undefined) => (email ? email.split('@')[0] : '');

/** Telefon do odkazu tel:/sms: – jen číslice a +. */
export const phoneHref = (phone: string) => phone.replace(/[^\d+]/g, '');
